import type { FormSchemaField, FormSchemaResolved } from '#layers/block-engine/types/form-schema';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { isFormSchemaDataField, mapFormSchemaEntries } from './entries';

/**
 * Builtin input block that renders each primitive field type. `enum` is handled
 * separately because it also derives `props.options` from the field's `values`.
 */
const DEFAULT_INPUT_BLOCK_BY_TYPE = {
	string: BuiltinBlockIds.Input,
	object: BuiltinBlockIds.Input,
	number: BuiltinBlockIds.InputNumber,
	boolean: BuiltinBlockIds.InputSwitch,
} as const;

/**
 * Fill in a default input block for every data field that declares a `type` but
 * no explicit `block` or `render`, so any schema-driven form renders a control
 * without each consumer re-implementing the type→control mapping. An explicit
 * `block` or `render` always wins; the default only fills the gap. Recurses into
 * structural entries (sections). Non-mutating: fields are copied, never modified
 * in place.
 */
export function applyDefaultInputBlocks<TResult extends object = Record<string, unknown>>(
	schema: FormSchemaResolved<TResult>,
): FormSchemaResolved<TResult> {
	return mapFormSchemaEntries(schema, (entry) => withDefaultInputBlock(entry));
}

function withDefaultInputBlock<TResult extends object>(entry: FormSchemaField<TResult>): FormSchemaField<TResult> {
	if (!isFormSchemaDataField<TResult>(entry)) {
		return entry;
	}

	if (('block' in entry && entry.block) || ('render' in entry && entry.render)) {
		return entry;
	}

	const type: string | undefined = 'type' in entry ? entry.type : undefined;

	if (type === 'enum') {
		const existingProps = 'props' in entry ? entry.props : undefined;
		const values = 'values' in entry ? entry.values : undefined;

		return {
			...entry,
			block: BuiltinBlockIds.InputSelect,
			props: {
				...existingProps,
				// Author-provided options win. Otherwise derive from `values` without a
				// label so the select input humanizes/translates each value itself.
				options: existingProps?.options ?? (Array.isArray(values) ? values.map((value) => ({ value })) : []),
			},
		} as FormSchemaField<TResult>;
	}

	if (type === 'string' || type === 'object' || type === 'number' || type === 'boolean') {
		return {
			...entry,
			block: DEFAULT_INPUT_BLOCK_BY_TYPE[type],
		} as FormSchemaField<TResult>;
	}

	return entry;
}
